// Shared "last successful sync" status line for the Gmail-listener and the
// Drive-subscription file trees. Both back onto the same
// {last_successful_sync_at, last_synced_at, last_sync_status, last_sync_error}
// shape, advanced at the BE ``_stamp_project_status`` chokepoint (only a clean
// "ok" moves last_successful_sync_at). Single-sourcing the indicator here keeps
// the two surfaces in agreement: change the copy or the status mapping once and
// both trees pick it up.
//
// The point of the split between "last successful" and "last attempt" is that
// last_synced_at is overwritten on EVERY run (incl. failures / rate-limits), so
// on its own it can't tell the operator when the listener last actually pulled
// cleanly. We show the last good pull, and when the most recent attempt did not
// succeed we say so and surface the error.
import type { CSSProperties } from "react";

import { describeNewFolders } from "../utils/describeNewFolders";

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const d = Math.round(h / 24);
    return `${d} day${d === 1 ? "" : "s"} ago`;
  } catch {
    return iso;
  }
}

// Friendly one-liner for a non-"ok" terminal status. Covers both the Gmail
// (auth_failed / quota_exceeded / truncated / error) and the Drive
// (auth_failed / folder_gone / drive_error / rate_limited / error) status
// vocabularies. Gmail labels its rate-limit "quota_exceeded"; Drive uses
// "rate_limited" - both map to the same operator-facing "rate-limited".
function attemptStatusLabel(status: string): string {
  switch (status) {
    case "auth_failed":
      return "needs reconnect";
    case "quota_exceeded":
    case "rate_limited":
      return "rate-limited";
    case "truncated":
      return "partial - run again";
    case "folder_gone":
      return "folder not found in Drive";
    case "drive_error":
      return "Drive error";
    default:
      return "failed";
  }
}

const MAX_ERROR_CHARS = 240;

interface Props {
  lastSuccessfulSyncAt: string | null | undefined;
  lastSyncedAt: string | null | undefined;
  lastSyncStatus: string | null | undefined;
  lastSyncError: string | null | undefined;
  // Drive only: the last walk was too large to fully scan in the scheduled
  // poll's budget. The sync still succeeded (status "ok") for the walked prefix,
  // so this is an amber note, not a failure - run a manual sync (larger budget).
  walkTruncated?: boolean;
  // Paths of subfolders that started syncing in the last sync. Gmail passes [].
  newFolders: string[];
  style?: CSSProperties;
}

export const SyncStatusLine = ({
  lastSuccessfulSyncAt,
  lastSyncedAt,
  lastSyncStatus,
  lastSyncError,
  walkTruncated,
  newFolders,
  style,
}: Props) => {
  const newFoldersText = describeNewFolders(newFolders);
  const failLabel =
    lastSyncStatus && lastSyncStatus !== "ok"
      ? attemptStatusLabel(lastSyncStatus)
      : null;
  const errorText =
    failLabel && lastSyncError
      ? lastSyncError.length > MAX_ERROR_CHARS
        ? `${lastSyncError.slice(0, MAX_ERROR_CHARS - 1)}…`
        : lastSyncError
      : null;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 400,
        color: "#6b7280",
        display: "inline-flex",
        alignItems: "baseline",
        gap: 6,
        flexWrap: "wrap",
        ...style,
      }}
    >
      <span
        title={
          lastSuccessfulSyncAt
            ? `Last successful sync: ${new Date(lastSuccessfulSyncAt).toLocaleString()}`
            : "This subscription has not completed a successful sync yet"
        }
      >
        Last successful sync: {relativeTime(lastSuccessfulSyncAt)}
      </span>
      {newFoldersText && <span>· {newFoldersText}</span>}
      {failLabel && (
        <span style={{ color: "#b91c1c", fontWeight: 500 }}>
          · last attempt {relativeTime(lastSyncedAt)}: {failLabel}
        </span>
      )}
      {errorText && (
        <span
          style={{ color: "#b91c1c", flexBasis: "100%", fontWeight: 400 }}
          title={lastSyncError ?? undefined}
        >
          {errorText}
        </span>
      )}
      {walkTruncated && (
        <span
          style={{ color: "#b45309", flexBasis: "100%", fontWeight: 500 }}
          title="The folder tree was too large to fully scan in the scheduled sync. Run a manual sync (it has a larger budget) to scan the rest."
        >
          · folder tree too large to fully scan - run a manual sync
        </span>
      )}
    </span>
  );
};

export default SyncStatusLine;
